import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../lib/firebase";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { Link } from "react-router-dom";
import Header from "./Header";

function IncomeExpenseSummary() {
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState("");   // YYYY-MM-DD
  const [incomeTotal, setIncomeTotal] = useState(0);
  const [expenseTotal, setExpenseTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "TOC > Expense Summary";
  }, []);


  const profit = useMemo(() => incomeTotal - expenseTotal, [incomeTotal, expenseTotal]);

  useEffect(() => {
    async function fetchBetween() {
      setError("");
      if (!startDate || !endDate) return;
      try {
        setLoading(true);
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        const q = query(
          collection(db, "records"),
          where("userId", "==", auth.currentUser?.uid || "anonymous"),
          where("date", ">=", start),
          where("date", "<=", end),
          orderBy("date", "asc")
        );
        const snap = await getDocs(q);
        let income = 0;
        let expense = 0;
        snap.forEach((doc) => {
          const d = doc.data();
          const amt = Number(d.amount) || 0;
          if ((d.type || "").toLowerCase() === "income") income += amt;
          else expense += amt; // default treat as expense if not labeled
        });
        setIncomeTotal(income);
        setExpenseTotal(expense);
      } catch (e) {
        setError("Failed to load data. Check Firestore rules/config.");
      } finally {
        setLoading(false);
      }
    }
    fetchBetween();
  }, [startDate, endDate]);

  const rangeLabel = useMemo(() => {
    if (!startDate || !endDate) return "mm/dd/yyyy - mm/dd/yyyy";
    const s = new Date(startDate);
    const e = new Date(endDate);
    const fmt = (d) => (
      (d.getMonth() + 1).toString().padStart(2, "0") + "/" +
      d.getDate().toString().padStart(2, "0") + "/" +
      d.getFullYear()
    );
    return `${fmt(s)} - ${fmt(e)}`;
  }, [startDate, endDate]);

  const fmtMoney = (n) => {
    const val = Number(n) || 0;
    return "$" + val.toFixed(2);
  };

  return (
    <div className="w-full max-w-full sm:max-w-[500px] min-h-screen md:min-h-[550px] flex-col bg-pink-50 flex items-center mx-auto">
      <Header />

      <div className="w-full px-2 sm:px-4 py-3 sm:py-6">
        <div className="bg-[#f3ebe3] rounded-md shadow-xl border border-gray-300 overflow-hidden">
          {/* Title */}
          <div className="bg-[#f7adc4] text-white py-3 flex justify-center items-center px-4">
            <h2 className="font-semibold text-sm sm:text-base md:text-[18px] text-center tracking-wide">
              INCOME & EXPENSE TRACKER
            </h2>
          </div>

          {/* Content Section */}
          <div className="bg-[#f3ebe3] p-3 sm:p-4">
            {/* Animated Back Button */}
            <Link
              to="/widget"
              className="w-10 h-10 text-[#f7adc4] rounded-full font-semibold transition transform hover:-translate-x-0.5 hover:scale-[1.02] active:scale-[0.98] flex items-center mb-2"
            >
              <svg
                className="h-8 w-8 transition-transform group-hover:-translate-x-1"
                viewBox="0 0 20 20"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M7.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4A1 1 0 018.707 6.293L6.414 8.586H17a1 1 0 110 2H6.414l2.293 2.293a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </Link>
            <div className="text-center py-4 sm:py-6 px-2 sm:px-4 rounded-md">
              {/* Date Range */}
              <div className="flex items-center justify-center gap-2 mb-3 flex-wrap">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-white border border-gray-300 text-gray-700 text-xs px-2 py-1 w-full sm:w-auto"
                />
                <span className="text-gray-600 text-xs">to</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-white border border-gray-300 text-gray-700 text-xs px-2 py-1 w-full sm:w-auto"
                />
              </div>

              <p className="text-gray-600 mb-4 sm:mb-6 text-xs sm:text-sm">
                {rangeLabel}
              </p>

              {error && (
                <p className="text-red-600 text-xs mb-2">{error}</p>
              )}

              {/* Income */}
              <div className="mb-4">
                <p className="text-gray-800 font-semibold mb-1">INCOME</p>
                <div className="bg-white border border-gray-300 rounded-md py-2 text-lg font-bold text-gray-700">
                  {loading ? "…" : fmtMoney(incomeTotal)}
                </div>
              </div>

              {/* Expenses */}
              <div className="mb-4">
                <p className="text-gray-800 font-semibold mb-1">EXPENSES</p>
                <div className="bg-white border border-gray-300 rounded-md py-2 text-lg font-bold text-gray-700">
                  {loading ? "…" : fmtMoney(expenseTotal)}
                </div>
              </div>

              {/* Profit */}
              <div>
                <p className="text-gray-800 font-semibold mb-1">PROFIT</p>
                <div className="bg-white border border-gray-300 rounded-md py-2 text-lg font-bold text-gray-700">
                  {loading ? "…" : fmtMoney(profit)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full bg-[#f7adc4] text-white text-center flex justify-center items-center px-4 py-2 mt-auto">
        <img src="/1.png" alt="Logo" className="h-auto w-16 sm:w-20" />
      </div>
    </div>
  );
}

export default IncomeExpenseSummary;
